import {
  Edit,
  GroupsOutlined,
  NotificationsOutlined,
  PersonOutlined,
  SettingsOutlined,
} from "@mui/icons-material";
import {
  Button,
  Card,
  FormControlLabel,
  Link,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { ROUTES } from "src/config/navigation/routes";
import GenericModal from "src/components/GenericModal/GenericModal";
import useUpdateUserData from "src/components/ManageOrg/hooks/useUpdateUserData";
import { PageBreadcrumb } from "src/components/PageBreadcrumb";
import { PageHeader } from "src/components/PageHeader/PageHeader";
import { useSnackbar } from "src/components/Snackbar";
import { theme } from "src/config/theme";
import { useUpdateUserMeMutation } from "src/hooks/mutations";
import { useGetUser } from "src/hooks/queries";
import { User } from "src/stores/models/user";
import { getOrgMembership } from "src/utils/user";
import { FunctionalGroupChips } from "../components/FunctionalGroups/FunctionalGroupChips";
import { IntegrationsPanel } from "../components/Integrations/IntegrationsPanel";

type NotificationPreferenceKey =
  | "weeklyDigestEnabled"
  | "commentMentionsEnabled"
  | "commentRepliesEnabled"
  | "approvalRequestsEnabled"
  | "approvalsCompletedEnabled"
  | "taskAssignmentsEnabled"
  | "taskStatusChangesEnabled"
  | "taskUpdatesEnabled";

const notificationGroups: {
  title: string;
  description: string;
  preferences: {
    key: NotificationPreferenceKey;
    label: string;
  }[];
}[] = [
  {
    title: "Comments",
    description: "Control emails for mentions and active comment threads.",
    preferences: [
      {
        key: "commentMentionsEnabled",
        label: "Email me when someone mentions me in a comment",
      },
      {
        key: "commentRepliesEnabled",
        label: "Email me when someone replies to a thread I'm in",
      },
    ],
  },
  {
    title: "Approvals",
    description: "Choose which document approval emails you receive.",
    preferences: [
      {
        key: "approvalRequestsEnabled",
        label: "Email me when a document is sent to me for approval",
      },
      {
        key: "approvalsCompletedEnabled",
        label: "Email me when my document is fully approved",
      },
    ],
  },
  {
    title: "Tasks",
    description: "Manage assignment and task activity emails.",
    preferences: [
      {
        key: "taskAssignmentsEnabled",
        label: "Email me when a task is assigned to me",
      },
      {
        key: "taskStatusChangesEnabled",
        label: "Email me when one of my tasks changes status",
      },
      {
        key: "taskUpdatesEnabled",
        label: "Email me when one of my tasks is updated",
      },
    ],
  },
  {
    title: "Digest",
    description: "Weekly summary emails are configured separately from live notifications.",
    preferences: [
      {
        key: "weeklyDigestEnabled",
        label: "Receive weekly digest emails",
      },
    ],
  },
];

const UserFunctionalGroupsCard = ({
  orgId,
  user,
}: {
  orgId: string;
  user?: User;
}) => {
  const memberships =
    getOrgMembership(user, orgId)?.functionalGroupMemberships ?? [];

  if (memberships.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Not a member of any groups. Add yourself to a group in{" "}
        <Link
          component={RouterLink}
          to={ROUTES.MANAGE_ORGANIZATION.replace(":orgId", orgId)}
        >
          organization settings
        </Link>
        .
      </Typography>
    );
  }

  return <FunctionalGroupChips memberships={memberships} />;
};

export const UserSettings: React.FC = () => {
  const { orgId = "" } = useParams();
  const { data: user, isLoading: isLoadingUser } = useGetUser();
  const [isAddMcpModalOpen, setIsAddMcpModalOpen] = useState(false);
  const { updateUserDataResponse } = useUpdateUserData({
    orgId,
  });
  const { showSnackbar } = useSnackbar();
  const updateUserMeMutation = useUpdateUserMeMutation();

  const handleNotificationToggle = async (
    preferenceKey: NotificationPreferenceKey,
    checked: boolean
  ) => {
    try {
      await updateUserMeMutation.mutateAsync({
        [preferenceKey]: checked,
      } as Partial<User>);
    } catch {
      showSnackbar("Failed to update notification settings");
    }
  };

  return (
    <div className="flex flex-col self-center w-full gap-6">
      <PageHeader
        pageTitleComponent={
          <PageBreadcrumb items={[{ label: "User Settings" }]} />
        }
      />

      <div className="flex flex-col gap-3 py-2 px-3">
        <div className="flex items-center gap-2">
          <PersonOutlined />
          <Typography variant="body1" data-testid="profile-subheader">
            Profile
          </Typography>
        </div>
        <Card
          className="flex flex-col"
          sx={{
            border: "1px solid rgba(224, 224, 224, 1)",
            fontSize: "1rem",
            boxShadow: "none",
            lineHeight: 2,
            padding: "16px",
            backgroundColor: "transparent",
            gap: "8px",
          }}
        >
          <div className="flex gap-2.5 items-center">
            <div className="font-bold">Name:</div>
            {user?.firstName} {user?.lastName}
            <Edit
              fontSize="small"
              cursor="pointer"
              data-testid={`edit-${user?.email}`}
              style={{ color: theme.palette.grey[500] }}
              onClick={() => {
                updateUserDataResponse.setSelectedUser(user);
                updateUserDataResponse.setOpenUpdateUserModal(true);
              }}
            />
          </div>
          <div className="flex gap-2 items-center">
            <div className="font-bold">Email:</div>
            {user?.email}
          </div>
        </Card>
      </div>

      <div className="flex flex-col gap-3 py-2 px-3">
        <div className="flex items-center gap-2">
          <GroupsOutlined />
          <Typography
            variant="body1"
            data-testid="functional-groups-subheader"
          >
            Functional Groups
          </Typography>
        </div>
        <Card
          className="flex flex-col"
          sx={{
            border: "1px solid rgba(224, 224, 224, 1)",
            fontSize: "1rem",
            boxShadow: "none",
            lineHeight: 2,
            padding: "16px",
            backgroundColor: "transparent",
            gap: "8px",
          }}
        >
          <UserFunctionalGroupsCard orgId={orgId} user={user} />
        </Card>
      </div>

      <div className="flex flex-col gap-3 py-2 px-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <SettingsOutlined />
            <Typography variant="body1" data-testid="integrations-subheader">
              Integrations
            </Typography>
          </div>
          <Button
            variant="contained"
            size="small"
            onClick={() => setIsAddMcpModalOpen(true)}
          >
            Add Integration
          </Button>
        </div>
        <Card
          className="flex flex-col"
          sx={{
            fontSize: "1rem",
            boxShadow: "none",
            lineHeight: 2,
            backgroundColor: "transparent",
          }}
        >
          <IntegrationsPanel
            isAddMcpModalOpen={isAddMcpModalOpen}
            onCloseAddMcpModal={() => setIsAddMcpModalOpen(false)}
          />
        </Card>
      </div>

      <div className="flex flex-col gap-3 py-2 px-3">
        <div className="flex items-center gap-2">
          <NotificationsOutlined />
          <Typography
            variant="body1"
            data-testid="notification-settings-subheader"
          >
            Notification Settings
          </Typography>
        </div>
        <Card
          className="flex flex-col"
          sx={{
            border: "1px solid rgba(224, 224, 224, 1)",
            fontSize: "1rem",
            boxShadow: "none",
            lineHeight: 2,
            padding: "16px",
            backgroundColor: "transparent",
            gap: "4px",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Notification emails can be turned on or off by category. Invite and
            PIN reset emails are always sent.
          </Typography>
          {notificationGroups.map((group) => (
            <div key={group.title} className="flex flex-col gap-1 py-2">
              <Typography variant="subtitle2">{group.title}</Typography>
              <Typography variant="body2" color="text.secondary">
                {group.description}
              </Typography>
              {group.preferences.map((preference) => (
                <FormControlLabel
                  key={preference.key}
                  control={
                    <Switch
                      checked={user?.[preference.key] ?? true}
                      disabled={
                        isLoadingUser || !user || updateUserMeMutation.isPending
                      }
                      onChange={(_, checked) =>
                        handleNotificationToggle(preference.key, checked)
                      }
                    />
                  }
                  label={preference.label}
                />
              ))}
            </div>
          ))}
        </Card>
      </div>

      <GenericModal
        isOpen={updateUserDataResponse.openUpdateUserModal}
        handleCancel={updateUserDataResponse.handleCloseUpdateUserModal}
        handleSave={() =>
          updateUserDataResponse.handleUpdateUser({ userId: user?.id || "" })
        }
        isDisabled={!updateUserDataResponse.areAllUpdateUserInputsFilled}
        title="Update User"
      >
        <div className="grid grid-cols-2 gap-3">
          <TextField
            label="First Name"
            size="small"
            fullWidth
            defaultValue={updateUserDataResponse.selectedUser?.firstName ?? ""}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              updateUserDataResponse.setUserToUpdate({
                lastName: updateUserDataResponse.selectedUser?.lastName ?? "",
                firstName: event.target.value,
              });
            }}
            style={{ padding: 0 }}
          />
          <TextField
            label="Last Name"
            size="small"
            fullWidth
            defaultValue={updateUserDataResponse.selectedUser?.lastName ?? ""}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              updateUserDataResponse.setUserToUpdate({
                firstName: updateUserDataResponse.selectedUser?.firstName ?? "",
                lastName: event.target.value,
              })
            }
          />
        </div>
      </GenericModal>
    </div>
  );
};
